import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import './PayrollPage.css';

const pad = (n) => String(n).padStart(2, '0');
const toDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => toDate(new Date());
const shiftDate = (s, days) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + days); return toDate(d); };
const monthOf = (s) => s.slice(0, 7);
const rupees = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const niceDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const niceMonth = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

const STATUS = [
  { id: 'present', label: 'Present' },
  { id: 'half', label: 'Half day' },
  { id: 'absent', label: 'Absent' },
  { id: 'off', label: 'Off' },
];

export default function PayrollPage() {
  const [tab, setTab] = useState('attendance');
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState('');

  const loadEmployees = useCallback(
    () => api.get('/staff/employees?all=1').then(setEmployees).catch((e) => setError(e.message)),
    []
  );
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const active = employees.filter((e) => e.is_active);

  return (
    <div className="py-root">
      <header className="py-head">
        <div>
          <h1>Attendance &amp; salary</h1>
          <p>Salary = monthly salary ÷ (days in month − paid leave days) × days worked. Days worked = Present + half days × ½. Off, Absent and unmarked days are not paid, so mark attendance every day.</p>
        </div>
      </header>

      <div className="py-tabs" role="tablist">
        <button type="button" className={tab === 'attendance' ? 'is-active' : ''} onClick={() => setTab('attendance')}>Daily attendance</button>
        <button type="button" className={tab === 'salary' ? 'is-active' : ''} onClick={() => setTab('salary')}>Monthly salary</button>
        <button type="button" className={tab === 'staff' ? 'is-active' : ''} onClick={() => setTab('staff')}>
          Staff {active.length > 0 && <b>{active.length}</b>}
        </button>
      </div>

      {error && <div className="py-error" onClick={() => setError('')}>{error}</div>}

      {tab === 'attendance' && <AttendanceTab employees={active} onError={setError} goStaff={() => setTab('staff')} />}
      {tab === 'salary' && <SalaryTab onError={setError} />}
      {tab === 'staff' && <StaffTab employees={employees} reload={loadEmployees} onError={setError} />}
    </div>
  );
}

/* ================= Daily attendance ================= */

function AttendanceTab({ employees, onError, goStaff }) {
  const [date, setDate] = useState(todayStr());
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/staff/attendance?date=${date}`)
      .then((rows) => { const m = {}; rows.forEach((r) => { m[r.employee_id] = r.status; }); setMarks(m); })
      .catch((e) => onError(e.message));
  }, [date, onError]);
  useEffect(() => { load(); }, [load]);

  const visible = employees.filter((e) => !e.join_date || e.join_date <= date);

  const mark = async (emp, status) => {
    const next = marks[emp.id] === status ? '' : status; // tap again to clear
    setMarks((m) => ({ ...m, [emp.id]: next }));
    try {
      await api.post('/staff/attendance', { date, employee_id: emp.id, status: next });
    } catch (e) { onError(e.message); load(); }
  };

  const markAll = async () => {
    setBusy(true);
    try { await api.post('/staff/attendance/bulk', { date, status: 'present' }); load(); }
    catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { present: 0, half: 0, absent: 0, off: 0, none: 0 };
    visible.forEach((e) => { c[marks[e.id] || 'none'] += 1; });
    return c;
  }, [visible, marks]);

  if (employees.length === 0) {
    return (
      <div className="py-empty">
        <strong>No staff added yet</strong>
        <span>Add your staff and their monthly salary first.</span>
        <button type="button" className="py-primary" onClick={goStaff}>Add staff</button>
      </div>
    );
  }

  return (
    <>
      <div className="py-toolbar">
        <div className="py-daynav">
          <button type="button" onClick={() => setDate(shiftDate(date, -1))} aria-label="Previous day">‹</button>
          <input type="date" value={date} max={todayStr()} onChange={(e) => e.target.value && setDate(e.target.value)} />
          <button type="button" onClick={() => setDate(shiftDate(date, 1))} disabled={date >= todayStr()} aria-label="Next day">›</button>
        </div>
        <span className="py-dayname">{niceDate(date)}</span>
        <button type="button" className="py-primary" onClick={markAll} disabled={busy || counts.none === 0}>
          Mark {counts.none > 0 ? `${counts.none} unmarked` : 'all'} as present
        </button>
      </div>

      <div className="py-counts">
        <span className="c-present">{counts.present} present</span>
        <span className="c-half">{counts.half} half day</span>
        <span className="c-absent">{counts.absent} absent</span>
        <span className="c-off">{counts.off} off</span>
        {counts.none > 0 && <span className="c-none">{counts.none} not marked</span>}
      </div>

      <div className="py-list">
        {visible.map((emp) => (
          <div key={emp.id} className={`py-row ${marks[emp.id] ? `is-${marks[emp.id]}` : ''}`}>
            <div className="py-who">
              <strong>{emp.name}</strong>
              <span>{emp.role || 'Staff'}</span>
            </div>
            {/* ADVANCE-FROM-DAILY */}<button type="button" onClick={async () => {
                const amt = window.prompt(`Advance for ${emp.name} on ${date} (₹)`);
                if (!amt || !(Number(amt) > 0)) return;
                const upi = window.confirm('Paid by UPI?\n\nOK = UPI   ·   Cancel = Cash');
                const note = window.prompt('Note (optional)') || '';
                try {
                  await api.post('/staff/advances', { employee_id: emp.id, date, amount: Number(amt), mode: upi ? 'upi' : 'cash', note });
                  window.alert(`₹${Number(amt).toLocaleString('en-IN')} advance saved for ${emp.name} (${upi ? 'UPI' : 'Cash'}). It will be deducted from this month's salary.`);
                } catch (e) { window.alert(e.message || 'Could not save the advance'); }
              }}
              style={{ marginRight: 12, height: 44, padding: '0 16px', borderRadius: 12, border: '1px solid #f4d7a8', background: '#fff7ea', color: '#8a4b00', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ₹ Advance
            </button>
            <div className="py-status" role="group" aria-label={`Attendance for ${emp.name}`}>
              {STATUS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`s-${s.id} ${marks[emp.id] === s.id ? 'is-on' : ''}`}
                  onClick={() => mark(emp, s.id)}
                  aria-pressed={marks[emp.id] === s.id}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ================= Monthly salary ================= */

function SalaryTab({ onError }) {
  const [month, setMonth] = useState(monthOf(todayStr()));
  const [rows, setRows] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [modes, setModes] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get(`/staff/salary?month=${month}`), api.get(`/staff/advances?month=${month}`)])
      .then(([s, a]) => { setRows(s); setAdvances(a); })
      .catch((e) => onError(e.message))
      .finally(() => setLoading(false));
  }, [month, onError]);
  useEffect(() => { load(); }, [load]);

  const pay = async (r) => {
    const mode = modes[r.employee_id] || 'cash';
    const warn = r.not_marked > 0 ? `\n\n${r.not_marked} day(s) are not marked and will not be deducted.` : '';
    if (!window.confirm(`Pay ${r.name} ${rupees(r.net)} for ${niceMonth(month)} by ${mode.toUpperCase()}?${warn}`)) return;
    try { await api.post('/staff/salary/pay', { employee_id: r.employee_id, month, mode, paid_on: todayStr() }); load(); }
    catch (e) { onError(e.message); }
  };

  const undo = async (r) => {
    if (!window.confirm(`Undo the salary payment for ${r.name}? The matching expense entry is removed too.`)) return;
    try { await api.post(`/staff/salary/pay/${r.payment.id}/undo`, {}); load(); }
    catch (e) { onError(e.message); }
  };

  const removeAdvance = async (a) => {
    if (!window.confirm(`Delete the ${rupees(a.amount)} advance for ${a.name}?`)) return;
    try { await api.post(`/staff/advances/${a.id}/delete`, {}); load(); }
    catch (e) { onError(e.message); }
  };

  const totals = rows.reduce((t, r) => ({
    earned: t.earned + r.earned, deduction: t.deduction + r.deduction,
    advances: t.advances + r.advances, net: t.net + r.net,
    paid: t.paid + (r.payment ? r.payment.net : 0),
  }), { earned: 0, deduction: 0, advances: 0, net: 0, paid: 0 });

  return (
    <>
      <div className="py-toolbar">
        <input type="month" value={month} max={monthOf(todayStr())} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <span className="py-dayname">{niceMonth(month)}</span>
        <button type="button" className="py-ghost" onClick={() => window.print()}>Print</button>
      </div>

      <div className="py-print-title">Ice Bay x Hendy's Kitchen: salary for {niceMonth(month)}</div>

      <div className="py-summary">
        <div><span>Salary earned</span><strong>{rupees(totals.earned)}</strong></div>
        <div><span>Advances given</span><strong>{rupees(totals.advances)}</strong></div>
        <div className="is-main"><span>Net to pay</span><strong>{rupees(totals.net)}</strong></div>
        <div><span>Already paid</span><strong>{rupees(totals.paid)}</strong></div>
      </div>

      {loading ? <div className="py-empty">Loading…</div> : rows.length === 0 ? (
        <div className="py-empty"><strong>No staff for this month</strong></div>
      ) : (
        <div className="py-table-wrap">
          <table className="py-table">
            <thead>
              <tr>
                <th>Staff</th>
                <th className="num">Monthly</th>
                <th className="num">Present</th>
                <th className="num">Half</th>
                <th className="num">Absent</th>
                <th className="num">Off</th>
                <th className="num">Days worked</th>
                <th className="num">Not marked</th>
                <th className="num">Advances</th>
                <th className="num">Net pay</th>
                <th className="py-noprint" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id}>
                  <td>
                    <button type="button" title="See attendance days" onClick={() => window.dispatchEvent(new CustomEvent('staff-history', { detail: { id: r.employee_id, name: r.name, month } }))} style={{ all: 'unset', cursor: 'pointer', fontWeight: 800, color: '#5b3fd9', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{r.name}</button>
                    <span className="py-sub">
                      {rupees(r.per_day)}/day ({rupees(r.monthly_salary)} ÷ {r.base_days})
                      {r.eligible_days < r.days_in_month && `, ${r.eligible_days} of ${r.days_in_month} days (joined mid-month)`}
                    </span>
                  </td>
                  <td className="num">{rupees(r.monthly_salary)}</td>
                  <td className="num">{r.present}</td>
                  <td className="num">{r.half}</td>
                  <td className="num">{r.absent}</td>
                  <td className="num">{r.off}</td>
                  <td className="num"><strong>{r.worked_days}</strong></td>
                  <td className={`num ${r.not_marked ? 'is-warn' : ''}`}>{r.not_marked}</td>
                  <td className="num">{r.advances ? `−${rupees(r.advances)}` : '—'}</td>
                  <td className="num"><strong>{rupees(r.payment ? r.payment.net : r.net)}</strong></td>
                  <td className="py-noprint py-pay-cell">
                    {r.payment ? (
                      <span className="py-paid">
                        Paid {r.payment.paid_on.slice(8)}/{r.payment.paid_on.slice(5, 7)}, {r.payment.mode}
                        <button type="button" onClick={() => undo(r)}>Undo</button>
                      </span>
                    ) : (
                      <span className="py-pay">
                        <select value={modes[r.employee_id] || 'cash'} onChange={(e) => setModes({ ...modes, [r.employee_id]: e.target.value })}>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="bank">Bank</option>
                        </select>
                        <button type="button" className="py-primary" onClick={() => pay(r)} disabled={r.net <= 0}>Pay</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="py-h2">Advances in {niceMonth(month)}</h2>
      {advances.length === 0 ? (
        <div className="py-muted">No advances this month. Give one from the Staff tab.</div>
      ) : (
        <div className="py-table-wrap">
          <table className="py-table">
            <thead><tr><th>Date</th><th>Staff</th><th>Note</th><th className="num">Amount</th><th className="py-noprint" /></tr></thead>
            <tbody>
              {advances.map((a) => (
                <tr key={a.id}>
                  <td>{a.date.slice(8)}/{a.date.slice(5, 7)}</td>
                  <td>{a.name}</td>
                  <td>{a.note || '—'} <span className="py-sub">{a.mode}</span></td>
                  <td className="num">{rupees(a.amount)}</td>
                  <td className="py-noprint"><button type="button" className="py-link-danger" onClick={() => removeAdvance(a)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ================= Staff list ================= */

const EMPTY = { name: '', phone: '', role: '', monthly_salary: '', join_date: todayStr(), paid_leaves: '4' };

function StaffTab({ employees, reload, onError }) {
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) return onError('Enter the staff name');
    if (!(Number(form.monthly_salary) >= 0) || form.monthly_salary === '') return onError('Enter the monthly salary');
    setSaving(true);
    try {
      if (editId) await api.post(`/staff/employees/${editId}`, form);
      else await api.post('/staff/employees', form);
      setForm(EMPTY); setEditId(null); reload();
    } catch (e) { onError(e.message); }
    finally { setSaving(false); }
  };

  const edit = (e) => {
    setEditId(e.id);
    setForm({ name: e.name, phone: e.phone || '', role: e.role || '', monthly_salary: String(e.monthly_salary), join_date: e.join_date || '', paid_leaves: String(e.paid_leaves != null ? e.paid_leaves : 4) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (e) => {
    const msg = e.is_active ? `Mark ${e.name} as left? Past attendance and payments are kept.` : `Bring ${e.name} back as active staff?`;
    if (!window.confirm(msg)) return;
    try { await api.post(`/staff/employees/${e.id}`, { is_active: !e.is_active }); reload(); }
    catch (err) { onError(err.message); }
  };

  const giveAdvance = async (e) => {
    const amount = window.prompt(`Advance amount for ${e.name} (₹)`);
    if (amount === null) return;
    if (!(Number(amount) > 0)) return onError('Enter a valid advance amount');
    const note = window.prompt('Note (optional)') || '';
    const upi = window.confirm('Paid by UPI? Press OK for UPI, Cancel for cash.');
    try {
      await api.post('/staff/advances', { employee_id: e.id, date: todayStr(), amount: Number(amount), mode: upi ? 'upi' : 'cash', note });
      window.alert(`Advance of ₹${Number(amount).toLocaleString('en-IN')} recorded for ${e.name}. It will be deducted from this month's salary.`);
    } catch (err) { onError(err.message); }
  };

  const set = (k) => (ev) => setForm({ ...form, [k]: ev.target.value });

  return (
    <>
      <div className="py-form">
        <h2 className="py-h2">{editId ? 'Edit staff member' : 'Add staff member'}</h2>
        <div className="py-form-grid">
          <label>Name<input value={form.name} onChange={set('name')} placeholder="Full name" /></label>
          <label>Role<input value={form.role} onChange={set('role')} placeholder="Cashier, Kitchen…" /></label>
          <label>Phone<input value={form.phone} onChange={set('phone')} inputMode="tel" placeholder="Optional" /></label>
          <label>Monthly salary (₹)<input value={form.monthly_salary} onChange={set('monthly_salary')} type="number" min="0" inputMode="decimal" /></label>
          <label>Joining date<input value={form.join_date} onChange={set('join_date')} type="date" /></label>
          <label>Paid leave days per month<input value={form.paid_leaves} onChange={set('paid_leaves')} type="number" min="0" step="0.5" inputMode="decimal" /></label>
        </div>
        <div className="py-form-actions">
          <button type="button" className="py-primary" onClick={submit} disabled={saving}>{editId ? 'Save changes' : 'Add staff'}</button>
          {editId && <button type="button" className="py-ghost" onClick={() => { setEditId(null); setForm(EMPTY); }}>Cancel</button>}
        </div>
      </div>

      <div className="py-table-wrap">
        <table className="py-table">
          <thead><tr><th>Name</th><th>Role</th><th>Phone</th><th className="num">Monthly salary</th><th>Joined</th><th /></tr></thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id} className={e.is_active ? '' : 'is-left'}>
                <td><strong>{e.name}</strong>{!e.is_active && <span className="py-sub">Left</span>}</td>
                <td>{e.role || '—'}</td>
                <td>{e.phone || '—'}</td>
                <td className="num">{rupees(e.monthly_salary)}</td>
                <td>{e.join_date ? `${e.join_date.slice(8)}/${e.join_date.slice(5, 7)}/${e.join_date.slice(0, 4)}` : '—'}</td>
                <td className="py-actions">
                  {e.is_active ? <button type="button" onClick={() => giveAdvance(e)}>Advance</button> : null}
                  <button type="button" onClick={() => edit(e)}>Edit</button>
                  <button type="button" className="py-link-danger" onClick={() => toggleActive(e)}>{e.is_active ? 'Mark as left' : 'Re-activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {employees.length === 0 && <div className="py-empty">No staff yet. Add the first one above.</div>}
      </div>
    </>
  );
}
